// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { AssetAssignmentList } from '@/audit/api/types';
import { Stack, Typography } from '@mui/material';
import { DateTime, Interval } from 'luxon';

interface AssetAssignmentItemProps {
  serial: string;
  start: DateTime;
  end?: DateTime;
  isStrikethrough?: boolean;
}

interface Props {
  property: AssetAssignmentList;
}

/**
 * Renders a single asset assignment item with its serial number and date range
 * @param serial - Serial number of the asset
 * @param start - Start date of the assignment
 * @param end - Optional end date of the assignment
 * @param isStrikethrough - Whether to render the text with a strikethrough style
 */
function AssetAssignmentItem(props: AssetAssignmentItemProps) {
  const dateDisplay = props.end
    ? Interval.fromDateTimes(props.start, props.end).toLocaleString({
        dateStyle: 'medium',
      })
    : props.start.toLocaleString({ dateStyle: 'medium' });

  return (
    <Typography
      variant="caption"
      marginLeft={2}
      sx={
        props.isStrikethrough ? { textDecoration: 'line-through' } : undefined
      }
    >
      {props.serial} as of {dateDisplay}
    </Typography>
  );
}

export function AuditAssetAssignmentListLabel(props: Props) {
  return (
    <Stack>
      <Typography component="span" variant="caption">
        <Typography component="span" variant="inherit" fontWeight="bold">
          {props.property.name}:{' '}
        </Typography>
        {props.property.newValue.length === 0 &&
          props.property.oldValue.length === 0 && (
            <Typography
              component="span"
              variant="inherit"
              color="text.secondary"
            >
              No assets
            </Typography>
          )}
      </Typography>
      {props.property.oldValue.map((item) => (
        <AssetAssignmentItem
          key={item.key}
          serial={item.serial}
          start={item.start}
          end={item.end}
          isStrikethrough
        />
      ))}
      {props.property.newValue.map((item) => (
        <AssetAssignmentItem
          key={item.key}
          serial={item.serial}
          start={item.start}
          end={item.end}
        />
      ))}
    </Stack>
  );
}
